import React, { ReactNode } from 'react';
import {
  MainFooter,
  FooterWrapper,
  FooterContent,
  FirstColumn,
  LogoStyle,
  LinksContainer,
  TextLinkStyle,
  SecondColumn,
  ContactContainer,
  SocialIconsLinksStyle,
  CopyrightTextStyle,
  LegalLinkStyle,
  AddressContainer,
  NavigationTextStyle,
} from './styles';
import { Link as GatsbyLink } from 'gatsby';
import { INavLink } from '../../types/contentfulContentTypes';
import ContentfulImage from '../ContentfulImage';
import { AnimatedLinks } from '../../styles/animatedLinks';
import NavLink from '../NavLink';

export interface FooterProps {
  footerLogoNavLink: INavLink;
  phoneNumber: string;
  email: string;
  navigationText: ReactNode;
  navLinks: INavLink[];
  legalLinks: INavLink[];
  resourceLinks: INavLink[];
  hideOnDesktop?: boolean;
  copyrightText?: string;
}

const Footer: React.FC<FooterProps> = (props) => {
  const {
    email,
    footerLogoNavLink,
    hideOnDesktop,
    legalLinks,
    resourceLinks,
    navigationText,
    navLinks,
    phoneNumber,
    copyrightText = 'Thrillworks, Inc',
  } = props;

  // Social Links
  const navItems = navLinks.map((navItem) => {
    return (
      <NavLink
        link={navItem}
        key={navItem.id}
        title={navItem.displayText ?? 'Thrillworks'}
      >
        <ContentfulImage
          image={navItem.icon}
          aria-label={navItem.displayText ?? 'Thrillworks Social Icon'}
        />
      </NavLink>
    );
  });

  // Legal Links
  const legalItems = legalLinks.map((legalItem) => {
    return (
      <NavLink link={legalItem} key={legalItem.id}>
        {legalItem.displayText}
      </NavLink>
    );
  });

  // Resource Links
  const resourceItems = resourceLinks.map((resourceItem) => {
    return (
      <NavLink link={resourceItem} key={resourceItem.id}>
        {resourceItem.displayText}
      </NavLink>
    );
  });

  // Current Year
  const currentYear = new Date().getFullYear();

  // Render
  return (
    <MainFooter hideOnDesktop={hideOnDesktop}>
      <FooterWrapper>
        <FooterContent>
          {/* First Column - Logo and links */}
          <FirstColumn aria-label="Footer Navigation">
            {/* Logo */}
            <LogoStyle>
              <GatsbyLink
                to={footerLogoNavLink.linkUrl ?? '/'}
                aria-label={
                  footerLogoNavLink?.icon?.description ?? 'Thrillworks Logo'
                }
              >
                <span className="sr-only" id="footerLogo">
                  {footerLogoNavLink.displayText || 'Thrillworks Logo'}
                </span>
                <ContentfulImage
                  image={footerLogoNavLink.icon}
                  alt={
                    footerLogoNavLink?.icon?.description ?? 'Thrillworks Logo'
                  }
                  aria-hidden="true"
                />
              </GatsbyLink>
            </LogoStyle>
            {/* Nav Collection */}
            <LinksContainer>
              <TextLinkStyle>
                <h2 className="title">Resources</h2>
                {resourceItems}
              </TextLinkStyle>
              <TextLinkStyle>
                <h2 className="title">Contact</h2>
                <a href={`tel:${phoneNumber}`}>{phoneNumber}</a>
                <a href={`mailto:${email}`}>{email}</a>
              </TextLinkStyle>
              <TextLinkStyle className="office-address">
                <h2 className="title">Office</h2>
                <p>711 Oval Court</p>
                <p>Burlington, ON L7L 6A9</p>
              </TextLinkStyle>
            </LinksContainer>
            {/* Contact info */}
            <ContactContainer>
              {/* Social icons */}
              <div>
                <SocialIconsLinksStyle>{navItems}</SocialIconsLinksStyle>
              </div>
              {/* Address */}
              <AddressContainer>
                {/* Copyright */}
                <CopyrightTextStyle>
                  &copy; {currentYear} &mdash; {copyrightText}{' '}
                </CopyrightTextStyle>
                <LegalLinkStyle>{legalItems}</LegalLinkStyle>
              </AddressContainer>
            </ContactContainer>
          </FirstColumn>

          {/* Second Column - Animated text and contact info */}
          <SecondColumn>
            <AnimatedLinks className="light">
              <NavigationTextStyle>{navigationText}</NavigationTextStyle>
            </AnimatedLinks>
          </SecondColumn>
        </FooterContent>
      </FooterWrapper>
    </MainFooter>
  );
};

export default Footer;
